import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import {
  getInternalTenantsOverview,
  getRecentInternalSupportActions,
  InternalSupportActionRow,
  InternalTenantOverviewRow,
  InternalTenantsOverviewKpis,
} from '../services/internalControlPlaneService';

type RecentFilter = 'all' | '7d' | '30d' | 'stale';
type AiHealthFilter = 'all' | 'fallback';
type EmailHealthFilter = 'all' | 'issue';

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const statusBadgeClass: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  trial: 'bg-blue-100 text-blue-700',
  expired: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
};

export const InternalTenantsOverviewView: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<InternalTenantOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState('all');
  const [status, setStatus] = useState('all');
  const [recentActivity, setRecentActivity] = useState<RecentFilter>('all');
  const [aiHealth, setAiHealth] = useState<AiHealthFilter>('all');
  const [emailHealth, setEmailHealth] = useState<EmailHealthFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [recentSupportActions, setRecentSupportActions] = useState<InternalSupportActionRow[]>([]);
  const [kpis, setKpis] = useState<InternalTenantsOverviewKpis>({
    activeTenants: 0,
    trialTenants: 0,
    paidTenants: 0,
    aiFallbackRate30d: 0,
    aiFallbackCount30d: 0,
    aiTotalQueries30d: 0,
    emailIssueRate30d: 0,
    emailIssueCount30d: 0,
    emailTotal30d: 0,
  });
  const [quickTenantId, setQuickTenantId] = useState('');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const [response, supportActions] = await Promise.all([
          getInternalTenantsOverview({
            plan,
            status,
            recentActivity,
            aiHealth,
            emailHealth,
            page,
            pageSize,
          }),
          getRecentInternalSupportActions(10),
        ]);
        if (cancelled) return;
        setRows(response.rows);
        setKpis(response.kpis);
        setTotalCount(response.totalCount);
        setRecentSupportActions(supportActions);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unable to load tenants.';
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [plan, status, recentActivity, aiHealth, emailHealth, page, pageSize]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalCount / pageSize)), [totalCount, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-800">Tenant overview unavailable</h3>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <button
          type="button"
          onClick={() => { setStatus('active'); setPlan('all'); setAiHealth('all'); setEmailHealth('all'); setPage(1); }}
          className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-indigo-300 transition-colors"
        >
          <p className="text-xs text-slate-500">Active tenants</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{kpis.activeTenants}</p>
        </button>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-xs text-slate-500">Trial / Paid mix</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{kpis.trialTenants} / {kpis.paidTenants}</p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setPlan('trial'); setStatus('all'); setAiHealth('all'); setEmailHealth('all'); setPage(1); }}
              className="text-xs px-2 py-1 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
            >
              Show trial
            </button>
            <button
              type="button"
              onClick={() => { setPlan('all'); setStatus('active'); setAiHealth('all'); setEmailHealth('all'); setPage(1); }}
              className="text-xs px-2 py-1 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
            >
              Show paid
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setAiHealth('fallback'); setEmailHealth('all'); setPage(1); }}
          className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-indigo-300 transition-colors"
        >
          <p className="text-xs text-slate-500">AI fallback rate (30d)</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{kpis.aiFallbackRate30d}%</p>
          <p className="text-xs text-slate-500 mt-1">{kpis.aiFallbackCount30d} / {kpis.aiTotalQueries30d} queries</p>
        </button>
        <button
          type="button"
          onClick={() => { setEmailHealth('issue'); setAiHealth('all'); setPage(1); }}
          className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-indigo-300 transition-colors"
        >
          <p className="text-xs text-slate-500">Email issue rate (30d)</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{kpis.emailIssueRate30d}%</p>
          <p className="text-xs text-slate-500 mt-1">{kpis.emailIssueCount30d} / {kpis.emailTotal30d} emails</p>
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select
            value={plan}
            onChange={(e) => { setPlan(e.target.value); setPage(1); }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="all">All plans</option>
            <option value="trial">Trial</option>
            <option value="tester">Tester</option>
            <option value="starter">Starter</option>
            <option value="pro">Pro</option>
            <option value="business">Business</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={recentActivity}
            onChange={(e) => { setRecentActivity(e.target.value as RecentFilter); setPage(1); }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            <option value="all">All activity</option>
            <option value="7d">Active in last 7d</option>
            <option value="30d">Active in last 30d</option>
            <option value="stale">Stale (&gt; 30d)</option>
          </select>

          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const normalized = quickTenantId.trim();
              if (!normalized) return;
              navigate(`/internal/tenants/${encodeURIComponent(normalized)}`);
            }}
          >
            <input
              type="text"
              value={quickTenantId}
              onChange={(e) => setQuickTenantId(e.target.value)}
              placeholder="tenant company UUID"
              className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button type="submit" className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm">
              <Search size={14} />
            </button>
          </form>
        </div>
        {(aiHealth !== 'all' || emailHealth !== 'all') && (
          <div className="mt-3 flex items-center gap-2">
            {aiHealth !== 'all' && <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">AI fallback only</span>}
            {emailHealth !== 'all' && <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">Email issue only</span>}
            <button
              type="button"
              onClick={() => { setAiHealth('all'); setEmailHealth('all'); setPage(1); }}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
            >
              Clear health filters
            </button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800">Recent support actions (plan overrides)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">When</th>
                <th className="text-left px-4 py-2">Tenant</th>
                <th className="text-left px-4 py-2">Transition</th>
                <th className="text-left px-4 py-2">Reason</th>
                <th className="text-right px-4 py-2">Open</th>
              </tr>
            </thead>
            <tbody>
              {recentSupportActions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-slate-500">No recent support actions.</td>
                </tr>
              ) : recentSupportActions.map((action) => (
                <tr key={action.request_id} className="border-t border-slate-100">
                  <td className="px-4 py-2 whitespace-nowrap">{formatDateTime(action.created_at)}</td>
                  <td className="px-4 py-2">
                    <p className="font-medium text-slate-800">{action.target_company_name || 'Unknown tenant'}</p>
                    <p className="text-xs text-slate-500">{action.target_company_id}</p>
                  </td>
                  <td className="px-4 py-2">
                    {(action.previous_plan || '—')} {'->'} {action.new_plan}
                  </td>
                  <td className="px-4 py-2 text-slate-700 max-w-[480px] truncate" title={action.reason}>{action.reason}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => navigate(`/internal/tenants/${encodeURIComponent(action.target_company_id)}/billing-actions`)}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
                    >
                      Billing actions
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 min-w-[220px]">Company</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Admins</th>
                <th className="text-left px-4 py-3 min-w-[170px]">Last activity</th>
                <th className="text-right px-4 py-3">AI queries</th>
                <th className="text-right px-4 py-3">Email (30d)</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8">
                    <EmptyState
                      title="No tenants found"
                      subtitle="Try adjusting the plan, status, or activity filters."
                    />
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.companyId} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{row.companyName}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{row.companyId}</p>
                  </td>
                  <td className="px-4 py-3 capitalize">{row.plan}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusBadgeClass[row.status] || 'bg-slate-100 text-slate-700'}`}>
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{row.activeAdmins}</td>
                  <td className="px-4 py-3">{formatDateTime(row.lastActivityAt)}</td>
                  <td className="px-4 py-3 text-right">{row.aiQueriesUsed}</td>
                  <td className="px-4 py-3 text-right">{row.emailsSentLast30d}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => navigate(`/internal/tenants/${encodeURIComponent(row.companyId)}`)}
                      className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100"
                    >
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Showing {rows.length} of {totalCount} tenants
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-sm disabled:opacity-40"
          >
            <ChevronLeft size={14} />
            Prev
          </button>
          <span className="text-sm text-slate-600">Page {page} / {totalPages}</span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-sm disabled:opacity-40"
          >
            Next
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};
